import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Building2, Warehouse, FileText, CalendarDays, Truck, Globe,
  AlertTriangle, CheckCircle2, Clock, XCircle, PlusCircle, Send
} from 'lucide-react';

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// For grand totals / payable / balance — rounded to nearest rupee, no paise
const moneyR = (v) => `₹${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;
const num = (v, d = 2) => Number(v || 0).toFixed(d);

const dateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

const dateOnly = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusMeta = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-700 border-gray-200', Icon: FileText },
  pending_verification: { label: 'Pending Warehouse Review', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Clock },
  completed: { label: 'Completed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', cls: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle }
};

const timelineMeta = {
  draft: { title: 'GRN created', Icon: PlusCircle, cls: 'bg-slate-100 text-slate-600' },
  pending_verification: { title: 'Sent for warehouse review', Icon: Send, cls: 'bg-amber-100 text-amber-700' },
  completed: { title: 'Verified & approved by warehouse', Icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700' },
  cancelled: { title: 'Cancelled', Icon: XCircle, cls: 'bg-red-100 text-red-700' }
};

const Field = ({ label, value, icon: Icon }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-gray-400 flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </p>
    <p className="text-sm font-medium text-gray-800 mt-0.5 break-words">{value || '-'}</p>
  </div>
);

const TotalRow = ({ label, value, tone = '' }) => (
  <div className="flex justify-between text-sm py-1">
    <span className="text-gray-500">{label}</span>
    <span className={`font-medium ${tone || 'text-gray-800'}`}>{value}</span>
  </div>
);

const SPChips = ({ item }) => {
  const chips = [];
  for (let n = 1; n <= 5; n++) {
    const price = item[`selling_price_${n}`];
    if (price == null) continue;
    const type = item[`pricing_type_${n}`];
    const pct = item[`pricing_percent_${n}`];
    chips.push(
      <span key={n} className="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-100 px-2 py-0.5 text-[11px] text-teal-700">
        SP{n} {money(price)}
        {type ? <span className="text-teal-500">· {type}{pct ? ` ${num(pct, 1)}%` : ''}</span> : null}
      </span>
    );
  }
  if (chips.length === 0) return <span className="text-xs text-gray-400">No selling price levels set</span>;
  return <div className="flex flex-wrap gap-1">{chips}</div>;
};

export const GRNDetailView = ({ grn }) => {
  if (!grn) return null;

  const isIntl = grn.grn_type === 'international' || Boolean(grn.currency && grn.currency !== 'INR');
  const meta = statusMeta[grn.status] || statusMeta.draft;
  const vendor = grn.vendor_snapshot || {};
  const history = [...(grn.status_history || [])].sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at));
  const disputes = grn.disputes || [];
  const currencyLabel = grn.currency || 'USD';

  return (
    <div className="space-y-6" data-testid="grn-detail-view">
      {/* Header */}
      <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-slate-50 to-white p-5">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{grn.grn_number}</h3>
          <Badge className={`${meta.cls} border`} data-testid="grn-detail-status">
            <meta.Icon className="w-3 h-3 mr-1" />{meta.label}
          </Badge>
          <Badge variant="outline" className="border-gray-300 text-gray-600">
            {isIntl ? <><Globe className="w-3 h-3 mr-1" />International</> : 'Local'}
          </Badge>
          {grn.has_disputes && (
            <Badge className="bg-orange-50 text-orange-700 border border-orange-200" data-testid="grn-detail-disputed-badge">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {grn.dispute_count} Disputed line{grn.dispute_count > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 mt-5">
          <Field label="Vendor" value={vendor.vendor_name || grn.vendor_name} icon={Building2} />
          <Field label="Vendor GSTIN" value={vendor.gstin} />
          <Field label="Warehouse" value={grn.warehouse_name} icon={Warehouse} />
          <Field label="PO Number" value={grn.po_number || 'Direct (No PO)'} icon={FileText} />
          <Field label="Invoice No" value={grn.invoice_number} />
          <Field label="DC / Challan No" value={grn.dc_number} />
          <Field label="Receipt Date" value={dateOnly(grn.received_date || grn.receipt_date)} icon={CalendarDays} />
          {isIntl ? (
            <Field label="Currency / Rate" value={`${currencyLabel} @ ${num(grn.conversion_rate, 2)}`} />
          ) : (
            <Field label="Mode of Receive" value={(grn.mode_of_receive || '').replace('_', ' ')} icon={Truck} />
          )}
          {isIntl && <Field label="Imported Date" value={dateOnly(grn.imported_date)} />}
          {vendor.address && <Field label="Vendor Address" value={vendor.address} />}
        </div>
      </div>

      {/* Disputes & adjustments */}
      {disputes.length > 0 && (
        <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-4" data-testid="grn-disputes-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <h4 className="font-semibold text-orange-800">Disputes & Adjustments</h4>
            <span className="text-xs text-orange-700">
              raised by {disputes[0]?.raised_by_name || 'warehouse'} on {dateTime(disputes[0]?.raised_at)}
            </span>
          </div>
          <div className="overflow-x-auto rounded-lg border border-orange-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-orange-50">
                  <TableHead className="text-orange-800">Product</TableHead>
                  <TableHead className="text-orange-800 text-center">HQ Qty</TableHead>
                  <TableHead className="text-orange-800 text-center">Warehouse Accepted</TableHead>
                  <TableHead className="text-orange-800 text-center">Variance</TableHead>
                  <TableHead className="text-orange-800 text-right">Value Impact</TableHead>
                  <TableHead className="text-orange-800">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {disputes.map((d, i) => (
                  <TableRow key={i} data-testid={`grn-dispute-row-${i}`}>
                    <TableCell className="text-gray-800">
                      {d.product_name}
                      <span className="block text-xs text-gray-400">{d.sku}</span>
                    </TableCell>
                    <TableCell className="text-center">{d.hq_accepted_qty}</TableCell>
                    <TableCell className="text-center font-medium">{d.verified_qty}</TableCell>
                    <TableCell className={`text-center font-medium ${d.variance_qty < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {d.variance_qty > 0 ? `+${d.variance_qty}` : d.variance_qty}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${d.variance_value < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {money(d.variance_value)}
                    </TableCell>
                    <TableCell className="text-gray-600 text-sm">{d.reason || 'Not specified'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap gap-6 mt-3 text-sm">
            <span className="text-orange-800">
              Total value impact: <strong>{money(grn.total_variance_value)}</strong>
            </span>
            {grn.original_grand_total != null && (
              <span className="text-orange-800">
                Invoiced {moneyR(grn.original_grand_total)} → Payable {moneyR(grn.grand_total)}
              </span>
            )}
            {grn.verification_notes && (
              <span className="text-orange-700">Warehouse note: {grn.verification_notes}</span>
            )}
          </div>
        </div>
      )}

      {/* Items */}
      <div>
        <h4 className="font-semibold text-gray-800 mb-2">Items ({grn.items?.length || 0})</h4>
        <div className="space-y-3">
          {(grn.items || []).map((item, i) => (
            <div key={i} className="rounded-xl border border-gray-200 overflow-hidden" data-testid={`grn-detail-item-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                <div>
                  <p className="font-medium text-gray-900">{item.product_name}</p>
                  <p className="text-xs text-gray-500">
                    SKU: {item.sku || item.product_sku || '-'} · HSN: {item.hsn_code || '-'} · UOM: {item.uom || '-'}
                  </p>
                  {item.scanned_barcode && (
                    <p className="text-xs text-indigo-600 font-mono mt-0.5" title="Barcode scanned on this GRN line">
                      📷 Scanned: {item.scanned_barcode}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {item.is_disputed && (
                    <Badge className="bg-orange-50 text-orange-700 border border-orange-200">
                      <AlertTriangle className="w-3 h-3 mr-1" />Disputed
                    </Badge>
                  )}
                  <span className="text-sm font-semibold text-gray-900">{money(item.total_amount ?? item.line_total)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-5 gap-y-3 px-4 py-3 text-sm">
                <Field label="Ordered" value={item.ordered_qty ?? 0} />
                <Field label="Received" value={item.received_qty ?? 0} />
                <Field label="Accepted" value={<span className="text-emerald-600">{item.accepted_qty ?? 0}</span>} />
                <Field label="Rejected" value={<span className={item.rejected_qty ? 'text-red-600' : ''}>{item.rejected_qty || 0}</span>} />
                {!!item.free_qty && (
                  <Field label="Free Qty" value={<span className="text-emerald-600 font-semibold">+{item.free_qty}</span>} />
                )}
                <Field label="Batch / Lot" value={item.lot_batch_number || item.batch_number} />
                <Field label="Expiry" value={dateOnly(item.expiry_date)} />
                <Field label="Mfg Date" value={dateOnly(item.manufacturing_date)} />
                <Field label="Best Before" value={dateOnly(item.best_before_date)} />

                {isIntl ? (
                  <>
                    <Field label={`Price (${item.currency || currencyLabel})`} value={num(item.unit_price_fcy)} />
                    <Field label="Conv. Rate" value={num(item.conversion_rate)} />
                    <Field label="Unit Price (INR)" value={money(item.unit_price_inr)} />
                    <Field label="Assessable" value={money(item.assessable_value)} />
                    <Field label={`Custom Duty ${num(item.custom_duty_percent, 1)}%`} value={money(item.custom_duty_amount)} />
                    <Field label={`SWC ${num(item.swc_percent, 1)}%`} value={money(item.swc_amount)} />
                    <Field label="Other Duty" value={money(item.other_duty_amount)} />
                    <Field label="FSSAI" value={money(item.fssai_charges)} />
                    <Field label="Landed / Unit" value={money(item.other_charges_per_qty)} />
                  </>
                ) : (
                  <>
                    <Field label="Unit Price" value={money(item.unit_price)} />
                    <Field label="Taxable" value={money(item.taxable_amount)} />
                  </>
                )}

                <Field label={`GST ${num(item.gst_percent, 2)}%`} value={money(item.gst_amount)} />
                <Field label="MRP" value={money(item.mrp)} />
                <Field label="Basic Cost" value={money(item.basic_cost ?? item.unit_price_inr)} />
                <Field label="Net / Landed Cost" value={money(item.net_cost ?? item.avg_base_cost)} />
              </div>

              {/* Discount breakdown - shows the 6-field Option A structure whenever any value is present. */}
              {(() => {
                const hasDisc = ['discount_percent','discount_amount','gross_discount_amount',
                                 'scheme_discount_percent','scheme_discount_amount','gross_scheme_discount_amount']
                                 .some(k => Number(item[k]) > 0);
                if (!hasDisc) return null;
                return (
                  <div className="mx-4 mb-3 rounded-lg bg-blue-50/60 border border-blue-100 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold mb-1.5">
                      Discount Breakdown
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-1.5 text-xs">
                      <div title="Discount % (per line)">
                        <span className="text-gray-500">Disc %</span>
                        <span className="ml-2 font-medium text-gray-800">{num(item.discount_percent, 2)}%</span>
                      </div>
                      <div title="Discount Amount (per unit)">
                        <span className="text-gray-500">Disc Amt</span>
                        <span className="ml-2 font-medium text-gray-800">{money(item.discount_amount)}</span>
                      </div>
                      <div title="Gross Discount Amount (line total = Qty x Disc Amt)">
                        <span className="text-gray-500">Gross Disc</span>
                        <span className="ml-2 font-medium text-gray-800">{money(item.gross_discount_amount)}</span>
                      </div>
                      <div title="Scheme Discount % (applied on post-discount unit price)">
                        <span className="text-gray-500">Sch %</span>
                        <span className="ml-2 font-medium text-gray-800">{num(item.scheme_discount_percent, 2)}%</span>
                      </div>
                      <div title="Scheme Discount Amount (per unit)">
                        <span className="text-gray-500">Sch Amt</span>
                        <span className="ml-2 font-medium text-gray-800">{money(item.scheme_discount_amount)}</span>
                      </div>
                      <div title="Gross Scheme Discount Amount (line total = Qty x Sch Amt)">
                        <span className="text-gray-500">Gross Sch</span>
                        <span className="ml-2 font-medium text-gray-800">{money(item.gross_scheme_discount_amount)}</span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-blue-200/70 text-xs text-blue-700">
                      Total line discount:{' '}
                      <span className="font-semibold">
                        {money((Number(item.gross_discount_amount) || 0) + (Number(item.gross_scheme_discount_amount) || 0))}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <div className="px-4 pb-3">
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Selling price levels</p>
                <SPChips item={item} />
              </div>

              {(item.rejection_reason || item.dispute_reason) && (
                <div className="px-4 pb-3 text-xs text-red-600">
                  {item.dispute_reason ? `Warehouse remark: ${item.dispute_reason}` : `Rejection reason: ${item.rejection_reason}`}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Totals + timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-800 mb-2">Bill Summary</h4>
          {isIntl && <TotalRow label={`Subtotal (${currencyLabel})`} value={num(grn.subtotal_fcy)} />}
          <TotalRow label="Subtotal (Taxable)" value={money(isIntl ? grn.subtotal_inr : grn.subtotal)} />
          {isIntl && <TotalRow label="Total Duty" value={money(grn.total_duty)} />}
          {isIntl && <TotalRow label="Landed Charges" value={money(grn.total_header_landed_cost)} />}
          <TotalRow
            label={`Bill Discount${grn.bill_discount_percent ? ` (${num(grn.bill_discount_percent, 2)}%)` : ''}`}
            value={`- ${money(grn.bill_discount_amount)}`}
            tone="text-red-600"
          />
          <TotalRow label="GST Total" value={money(grn.gst_total ?? grn.tax_total)} />
          {!isIntl && <TotalRow label="Other Charges (+)" value={money(grn.other_charges_add)} />}
          {!isIntl && <TotalRow label="Other Charges (-)" value={`- ${money(grn.other_charges_deduct)}`} tone="text-red-600" />}
          <TotalRow label="Round Off" value={money(grn.round_off)} />
          <Separator className="my-2" />
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-800">Grand Total</span>
            <span className="text-lg font-bold text-teal-600" data-testid="grn-detail-grand-total">{moneyR(grn.grand_total)}</span>
          </div>
          {grn.original_grand_total != null && grn.original_grand_total !== grn.grand_total && (
            <p className="text-xs text-orange-600 mt-1">
              Adjusted from {moneyR(grn.original_grand_total)} after warehouse verification
            </p>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 p-4" data-testid="grn-activity-history">
          <h4 className="font-semibold text-gray-800 mb-3">Activity & Approval History</h4>
          <ol className="space-y-4">
            {history.length === 0 && <li className="text-sm text-gray-400">No activity recorded</li>}
            {history.map((h, i) => {
              const tm = timelineMeta[h.to_status] || { title: h.to_status, Icon: Clock, cls: 'bg-gray-100 text-gray-600' };
              return (
                <li key={i} className="flex gap-3">
                  <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${tm.cls}`}>
                    <tm.Icon className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">{tm.title}</p>
                    <p className="text-xs text-gray-500">
                      by {h.changed_by_name || 'system'} · {dateTime(h.changed_at)}
                    </p>
                    {h.notes && <p className="text-xs text-gray-500 mt-0.5">{h.notes}</p>}
                  </div>
                </li>
              );
            })}
          </ol>
          <Separator className="my-3" />
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-gray-400 uppercase tracking-wide">Created</p>
              <p className="text-gray-700">{dateTime(grn.created_at)}</p>
            </div>
            <div>
              <p className="text-gray-400 uppercase tracking-wide">Verified / Approved</p>
              <p className="text-gray-700">
                {grn.verified_at ? `${grn.verified_by_name || '-'} · ${dateTime(grn.verified_at)}` : 'Awaiting warehouse'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {grn.notes && (
        <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-400">Notes</p>
          <p className="text-sm text-gray-700 whitespace-pre-line mt-1">{grn.notes}</p>
        </div>
      )}
    </div>
  );
};

export default GRNDetailView;
